import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { projects as projectsApi, clients as clientsApi } from '@/utils/api';
import { FolderOpen, Clock, FileText, Bug, ArrowRight, CircleDot } from 'lucide-react';

const STATUS_COLORS = {
  'New': '#5C6B72', 'Scoping': '#006080', 'Estimate Sent': '#D9A441',
  'Approved': '#3C8A5A', 'In Progress': '#006080', 'UAT': '#0066CC',
  'Delivered': '#3C8A5A', 'Archived': '#8A99A0',
};

export default function ClientPortal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      projectsApi.list(),
      clientsApi.list(),
    ]).then(([p, c]) => {
      setProjects(p.data);
      setClient(c.data[0] || null);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const greeting = () => {
    const h = new Date().getHours();
    return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
  };

  if (loading) return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <div className="h-8 w-48 rounded animate-pulse mb-8" style={{ background: 'var(--border)' }} />
      <div className="space-y-4">
        {[1, 2].map(i => (
          <div key={i} className="h-32 rounded-card animate-pulse" style={{ background: 'var(--border)' }} />
        ))}
      </div>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-6 py-12 animate-fade-in">
      {/* Welcome header */}
      <div className="mb-10">
        <h1 className="text-4xl font-black mb-2" style={{ color: 'var(--ink)' }}>
          {greeting()}, {user?.name?.split(' ')[0] || 'there'}
        </h1>
        {client?.company_name && (
          <p className="text-base" style={{ color: 'var(--muted)' }}>
            {client.company_name} — here's your current project status.
          </p>
        )}
      </div>

      {/* Projects */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold" style={{ color: 'var(--ink)' }}>
            Your Projects
          </h2>
          <span className="text-sm" style={{ color: 'var(--muted)' }}>
            {projects.length} project{projects.length !== 1 ? 's' : ''}
          </span>
        </div>

        {projects.length === 0 ? (
          <div
            className="card-base p-12 text-center"
          >
            <FolderOpen size={48} style={{ color: 'var(--border)', margin: '0 auto 16px' }} />
            <h3 className="font-bold text-lg mb-2" style={{ color: 'var(--ink)' }}>No projects yet</h3>
            <p style={{ color: 'var(--muted)' }}>
              Your operator will create a project for you after your intake is reviewed.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {projects.map(p => (
              <div
                key={p.id}
                className="card-interactive p-5"
                onClick={() => navigate(`/portal/projects/${p.id}`)}
                data-testid={`client-project-${p.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <CircleDot
                      size={16}
                      style={{ color: STATUS_COLORS[p.status] || '#006080', marginTop: 2, flexShrink: 0 }}
                    />
                    <div>
                      <h3 className="font-bold" style={{ color: 'var(--ink)' }}>{p.name}</h3>
                      <p className="text-sm mt-0.5" style={{ color: 'var(--muted)' }}>{p.project_type}</p>
                      {p.description && (
                        <p className="text-sm mt-2 line-clamp-2" style={{ color: 'var(--muted)' }}>{p.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    <span
                      className="badge-status"
                      style={{
                        background: `${STATUS_COLORS[p.status] || '#006080'}15`,
                        color: STATUS_COLORS[p.status] || '#006080',
                      }}
                    >
                      {p.status}
                    </span>
                    <ArrowRight size={16} style={{ color: 'var(--muted)' }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
